import React, { Component } from "react";
import { Button } from "reactstrap";
import AsesoresTable from "./Asesores.table";
import FormBuilderModal from "../../../common/formBuilderModal/formBuilderModal";
import ConfirmModal from "../../../common/confirmModal/confirmModal";
import { formConfig } from "./AgregarAsesor.form";
import { formConfig as homologarFormConfig } from "./HomologarCompromisos.form";
import {
  successNotification,
  errorNotification
} from "../../../../../utils/notifications";
import { userHasPermission } from "../../../../../utils/accessTree";
import permissionCodes from "../../../../../utils/permissionCodes";

const {
  TRACKING_VENTAS_MEMBRESIA_EDICION_COMPROMISOS_ASESORES: EDICION_COMPROMISOS_ASESORES
} = permissionCodes;

interface Props {
  periodo: string;
  idClub: number;
  trackingData: any;
  saveKey: number;
  puttingAsesores: boolean;
  onSave: (periodo: string, idClub: number, asesores: any[]) => Promise<any>;
  onReload: (periodo: string, idClub: number) => Promise<any>;
}

interface State {
  localAsesores: any[];
  hasChanges: boolean;
  formErrors: Set<number>;
  modalOpen: boolean;
  homologarModalOpen: boolean;
  confirmModalOpen: boolean;
  asesorToDelete: number | null;
  periodoAnterior: boolean;
}

export default class AsesoresComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const asesores = props.trackingData
      ? props.trackingData.asesores || []
      : [];

    this.state = {
      localAsesores: asesores,
      hasChanges: false,
      formErrors: new Set(),
      modalOpen: false,
      homologarModalOpen: false,
      confirmModalOpen: false,
      asesorToDelete: null,
      periodoAnterior: false
    };
  }

  componentDidMount() {
    this.calcPeriodoAnterior();
  }

  componentDidUpdate(prevProps: Props) {
    const asesores = this.props.trackingData
      ? this.props.trackingData.asesores
      : null;
    const prevAsesores = prevProps.trackingData
      ? prevProps.trackingData.asesores
      : null;

    if (asesores && asesores !== prevAsesores) {
      this.setState({
        localAsesores: asesores,
        hasChanges: false,
        formErrors: new Set()
      });
    }

    // Detectar periodo anterior
    const trackingData = this.props.trackingData;
    const prevTrackingData = prevProps.trackingData;

    if (trackingData && trackingData !== prevTrackingData) {
      this.calcPeriodoAnterior();
    }
  }

  calcPeriodoAnterior() {
    const trackingData = this.props.trackingData;

    if (trackingData) {
      // Periodo sin cambio de día
      const fechaPeriodo = new Date(`${trackingData.periodo}T00:00:00Z`);
      const mesPeriodo = fechaPeriodo.getUTCMonth();
      const anioPeriodo = fechaPeriodo.getUTCFullYear();

      // Fecha actual
      const fechaActual = new Date();
      const mesActual = fechaActual.getMonth();
      const anioActual = fechaActual.getFullYear();

      // Periodo anterior
      const esPeriodoAnterior =
        anioPeriodo < anioActual ||
        (anioPeriodo === anioActual && mesPeriodo < mesActual);

      this.setState({ periodoAnterior: esPeriodoAnterior });
    }
  }

  handleUpdate = (index: number, data: any, hasError: boolean) => {
    const { localAsesores, formErrors } = this.state;
    const newAsesores = [...localAsesores];
    newAsesores[index] = data;

    if (hasError) {
      formErrors.add(index);
    } else {
      formErrors.delete(index);
    }

    this.setState({
      localAsesores: newAsesores,
      hasChanges: true,
      formErrors
    });
  };

  handleOpenModal = () => {
    this.setState({ modalOpen: true });
  };

  handleCloseModal = () => {
    this.setState({ modalOpen: false });
  };

  handleOpenHomologarModal = () => {
    this.setState({ homologarModalOpen: true });
  };

  handleCloseHomologarModal = () => {
    this.setState({ homologarModalOpen: false });
  };

  handleHomologarCompromisos = async (values: any) => {
    const { localAsesores } = this.state;
    const { consolidado, totalVentas, numeroProspectos } = values;

    if (localAsesores.length === 0) {
      errorNotification("No hay asesores en la lista para homologar");
      return;
    }

    // Aplicar los compromisos a todos los asesores
    const asesoresActualizados = localAsesores.map(asesor => ({
      ...asesor,
      consolidado: Number(consolidado) || 0,
      totalVentas: Number(totalVentas) || 0,
      numeroProspectos: Number(numeroProspectos) || 0
    }));

    this.setState({
      localAsesores: asesoresActualizados,
      hasChanges: true,
      homologarModalOpen: false
    });

    // Guardar automáticamente
    const { periodo, idClub } = this.props;
    const preview = await this.props.onSave(
      periodo,
      idClub,
      asesoresActualizados
    );

    if (preview.fulfilled) {
      successNotification("Compromisos guardados correctamente");
      this.setState({ hasChanges: false });
      await this.props.onReload(periodo, idClub);
    } else {
      errorNotification(preview.puttingMembresiaTrackingAsesoresError);
    }
  };

  handleAgregarAsesor = async (values: any) => {
    const { localAsesores } = this.state;
    const { idUser, consolidado, totalVentas, numeroProspectos } = values;

    // Verificar si el asesor ya existe
    const asesorExiste = localAsesores.some(a => a.idUser === idUser);
    if (asesorExiste) {
      errorNotification("El asesor ya está en la lista");
      return;
    }

    // Crear nuevo asesor con valores por defecto
    const nuevoAsesor = {
      idUser: idUser,
      nombreCompleto: "Asesor nuevo", // Se actualizará al guardar
      consolidado: Number(consolidado) || 0,
      consolidadoActual: 0,
      consolidadoAlcance: 0,
      totalVentas: Number(totalVentas) || 0,
      totalVentasActual: 0,
      totalVentasAlcance: 0,
      numeroProspectos: Number(numeroProspectos) || 0,
      numeroProspectosActual: 0,
      numeroProspectosAlcance: 0
    };

    const asesoresActualizados = [...localAsesores, nuevoAsesor];

    this.setState({
      localAsesores: asesoresActualizados,
      hasChanges: true,
      modalOpen: false
    });

    // Guardar automáticamente
    const { periodo, idClub } = this.props;
    const asesoresData = asesoresActualizados.map(asesor => ({
      idUser: asesor.idUser,
      consolidado: asesor.consolidado,
      numeroProspectos: asesor.numeroProspectos,
      totalVentas: asesor.totalVentas || 0
    }));

    const preview = await this.props.onSave(periodo, idClub, asesoresData);

    if (preview.fulfilled) {
      successNotification("Asesor agregado y guardado correctamente");
      this.setState({ hasChanges: false });
      await this.props.onReload(periodo, idClub);
    } else {
      errorNotification(
        preview.puttingMembresiaTrackingAsesoresError ||
          "Error al guardar el asesor"
      );
    }
  };

  handleOpenConfirmDelete = (index: number) => {
    this.setState({
      confirmModalOpen: true,
      asesorToDelete: index
    });
  };

  handleCancelDelete = () => {
    this.setState({
      confirmModalOpen: false,
      asesorToDelete: null
    });
  };

  handleConfirmDelete = () => {
    const { localAsesores, asesorToDelete, formErrors } = this.state;

    if (asesorToDelete !== null) {
      const newAsesores = localAsesores.filter((_, i) => i !== asesorToDelete);

      // Actualizar índices de errores
      const newFormErrors = new Set<number>();
      formErrors.forEach(errorIndex => {
        if (errorIndex < asesorToDelete) {
          newFormErrors.add(errorIndex);
        } else if (errorIndex > asesorToDelete) {
          newFormErrors.add(errorIndex - 1);
        }
      });

      this.setState({
        localAsesores: newAsesores,
        hasChanges: true,
        formErrors: newFormErrors,
        confirmModalOpen: false,
        asesorToDelete: null
      });

      successNotification(
        "Debe guardar los cambios para finalizar la eliminación."
      );
    }
  };

  handleGuardar = async () => {
    const { periodo, idClub } = this.props;
    const { localAsesores, formErrors } = this.state;

    if (formErrors.size > 0) {
      const firstError = [...formErrors].sort((a, b) => a - b)[0];
      const asesor = localAsesores[firstError];
      errorNotification(
        `Valores erróneos en el asesor: ${asesor.nombreCompleto}`
      );
      return;
    }

    if (!periodo || !idClub) {
      errorNotification("No hay datos para guardar");
      return;
    }

    // Mapear a formato del backend
    const asesoresData = localAsesores.map(asesor => ({
      idUser: asesor.idUser,
      consolidado: asesor.consolidado,
      numeroProspectos: asesor.numeroProspectos,
      totalVentas: asesor.totalVentas || 0
    }));

    const preview = await this.props.onSave(periodo, idClub, asesoresData);

    if (preview.fulfilled) {
      successNotification("Asesores guardados exitosamente");
      this.setState({
        hasChanges: false,
        formErrors: new Set()
      });
      await this.props.onReload(periodo, idClub);
    } else {
      errorNotification(
        preview.puttingMembresiaTrackingAsesoresError ||
          "Error al guardar los asesores"
      );
    }
  };

  render() {
    const {
      localAsesores,
      hasChanges,
      modalOpen,
      homologarModalOpen,
      confirmModalOpen,
      periodoAnterior
    } = this.state;
    const { saveKey, puttingAsesores } = this.props;

    return (
      <div>
        <div className="row">
          <div className="col-8 mb-2">
            <h4>Resumen de Ventas por Asesor</h4>
          </div>
          <div className="col-4 text-right mb-2">
            {userHasPermission(EDICION_COMPROMISOS_ASESORES) && (
              <Button color="light" size="sm" onClick={this.handleOpenModal}>
                + Agregar Asesor
              </Button>
            )}
          </div>
        </div>

        <AsesoresTable
          data={this.props.trackingData}
          asesores={localAsesores}
          onUpdate={this.handleUpdate}
          onDelete={this.handleOpenConfirmDelete}
          saveKey={saveKey}
        />

        {userHasPermission(EDICION_COMPROMISOS_ASESORES) && (
          <div className="mt-3 d-flex justify-content-between">
            <Button
              color="info"
              onClick={this.handleOpenHomologarModal}
              disabled={
                localAsesores.length === 0 || puttingAsesores || periodoAnterior
              }
              size="sm"
            >
              Homologar Compromisos
            </Button>
            <Button
              color="secondary"
              onClick={this.handleGuardar}
              disabled={!hasChanges || puttingAsesores}
              size="sm"
            >
              {puttingAsesores ? "Guardando..." : "Guardar Asesores"}
            </Button>
          </div>
        )}

        <FormBuilderModal
          isOpen={modalOpen}
          title="Agregar Asesor"
          message="Complete la información del asesor"
          formConfig={formConfig}
          ok={this.handleAgregarAsesor}
          cancel={this.handleCloseModal}
        />

        <FormBuilderModal
          isOpen={homologarModalOpen}
          title="Homologar Compromisos"
          message="Estos compromisos se aplicarán a todos los asesores de la lista"
          formConfig={homologarFormConfig}
          ok={this.handleHomologarCompromisos}
          cancel={this.handleCloseHomologarModal}
        />

        <ConfirmModal
          isOpen={confirmModalOpen}
          title="Confirmar eliminación"
          message="Se borrará este asesor de la lista, pero debe oprimir el botón 'Guardar Asesores'."
          ok={this.handleConfirmDelete}
          cancel={this.handleCancelDelete}
          size="md"
        />
      </div>
    );
  }
}
